import { ArrowLeft, Building2, CheckCircle2, Clock, AlertTriangle, Users, TrendingUp, Settings, UserCog, UserPlus } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Progress } from './ui/progress';

interface ClientDetailHeaderProps {
  company: {
    id: string;
    name: string;
    logo?: string;
  };
  metrics: {
    totalItems: number;
    completedItems: number;
    waitingForConsultant: number;
    waitingForClient: number;
    blockedItems: number;
    progressPercentage: number;
    healthStatus: 'healthy' | 'at-risk' | 'needs-attention';
    spaces: number;
    users: number;
  };
  onBack: () => void;
  onManageSpaces?: () => void;
  onManageUsers?: () => void;
  onCompanySettings?: () => void;
  onShowBlockedItems?: () => void;
  onShowYourActionItems?: () => void;
  onShowClientActionItems?: () => void;
  onShowScopeControls?: (filter?: string) => void;
  scopeMetrics?: {
    undecided: number;
    changeOrder: number;
  };
  onViewAsClient?: () => void;
}

export function ClientDetailHeader({ company, metrics, onBack, onManageSpaces, onManageUsers, onCompanySettings, onShowBlockedItems, onShowYourActionItems, onShowClientActionItems, onShowScopeControls, scopeMetrics, onViewAsClient }: ClientDetailHeaderProps) {
  const getHealthBadge = (status: string) => {
    switch (status) {
      case 'healthy':
        return (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            <CheckCircle2 className="size-3 mr-1" />
            Healthy
          </Badge>
        );
      case 'at-risk':
        return (
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
            <AlertTriangle className="size-3 mr-1" />
            At Risk
          </Badge>
        );
      case 'needs-attention':
        return (
          <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
            <Clock className="size-3 mr-1" />
            Needs Attention
          </Badge>
        );
      default:
        return null;
    }
  };

  const getScopeBadge = () => {
      if (!scopeMetrics || !onShowScopeControls) return null;
      
      if (scopeMetrics.undecided > 0) {
          return (
              <Badge 
                variant="outline" 
                className="bg-purple-50 text-purple-700 border-purple-200 cursor-pointer hover:bg-purple-100"
                onClick={() => onShowScopeControls('undecided')}
              >
                  <AlertTriangle className="size-3 mr-1" />
                  Scope Review: {scopeMetrics.undecided}
              </Badge>
          );
      }
      
      if (scopeMetrics.changeOrder > 0) {
          return (
              <Badge 
                variant="outline" 
                className="bg-blue-50 text-blue-700 border-blue-200 cursor-pointer hover:bg-blue-100"
                onClick={() => onShowScopeControls('prepare_change_order')}
              >
                  <Clock className="size-3 mr-1" />
                  Change Order: {scopeMetrics.changeOrder}
              </Badge>
          );
      }
      
      return null;
  };

  return (
    <div className="space-y-4">
      {/* Back Button */}
      <Button variant="ghost" onClick={onBack} className="mb-2">
        <ArrowLeft className="size-4 mr-2" />
        Back to All Clients
      </Button>

      {/* Header Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              {company.logo ? (
                <img src={company.logo} alt={company.name} className="size-16 rounded object-cover" />
              ) : (
                <div className="size-16 rounded bg-blue-100 flex items-center justify-center">
                  <Building2 className="size-8 text-blue-600" />
                </div>
              )}
              <div>
                <h1 className="text-3xl font-bold mb-2">{company.name}</h1>
                <div className="flex items-center gap-3 flex-wrap">
                  {getHealthBadge(metrics.healthStatus)}
                  <Badge variant="outline" className="text-xs">
                    <Users className="size-3 mr-1" />
                    {metrics.spaces} {metrics.spaces === 1 ? 'Space' : 'Spaces'}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    {metrics.users} {metrics.users === 1 ? 'User' : 'Users'}
                  </Badge>
                  {getScopeBadge()}
                  {onShowScopeControls && (scopeMetrics?.undecided || 0) > 0 && (
                      <span 
                        className="text-xs text-blue-600 cursor-pointer hover:underline"
                        onClick={() => onShowScopeControls()}
                      >
                          Open Scope Inbox
                      </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              {onManageSpaces && (
                <Button variant="outline" onClick={onManageSpaces}>
                  <Settings className="size-4 mr-2" />
                  Manage Spaces
                </Button>
              )}
              {onViewAsClient && (
                <Button variant="outline" onClick={onViewAsClient} className="border-amber-200 text-amber-700 hover:bg-amber-50">
                  <UserCog className="size-4 mr-2" />
                  View As Client
                </Button>
              )}
              {onManageUsers && (
                <Button variant="outline" onClick={onManageUsers}>
                  <UserPlus className="size-4 mr-2" />
                  Invite client user
                </Button>
              )}
              {onCompanySettings && (
                <Button variant="outline" onClick={onCompanySettings}>
                  <Settings className="size-4 mr-2" />
                  Company Settings
                </Button>
              )}
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            {/* Overall Progress */}
            <div className="col-span-2 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-900">Overall Progress</span>
                <TrendingUp className="size-5 text-blue-600" />
              </div>
              <p className="text-3xl font-bold text-blue-600 mb-2">{metrics.progressPercentage}%</p>
              <Progress value={metrics.progressPercentage} className="h-2 mb-2" />
              <p className="text-xs text-blue-700">
                {metrics.completedItems} of {metrics.totalItems} items completed
              </p>
            </div>

            {/* Completed */}
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-green-900">Completed</span>
                <CheckCircle2 className="size-4 text-green-600" />
              </div>
              <p className="text-2xl font-bold text-green-600">{metrics.completedItems}</p>
              <p className="text-xs text-green-700 mt-1">
                {metrics.totalItems > 0 
                  ? Math.round((metrics.completedItems / metrics.totalItems) * 100)
                  : 0}% done
              </p>
            </div>

            {/* Your Action */}
            <div 
              className={`bg-orange-50 rounded-lg p-4 border border-orange-200 ${onShowYourActionItems && metrics.waitingForConsultant > 0 ? 'cursor-pointer hover:bg-orange-100 hover:shadow-md transition-all' : ''}`}
              onClick={() => {
                if (onShowYourActionItems && metrics.waitingForConsultant > 0) {
                  onShowYourActionItems();
                }
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-orange-900">Your Action</span>
                <Clock className="size-4 text-orange-600" />
              </div>
              <p className="text-2xl font-bold text-orange-600">{metrics.waitingForConsultant}</p>
              <p className="text-xs text-orange-700 mt-1">
                {metrics.waitingForConsultant > 0 && onShowYourActionItems ? 'Click to review' : 'Need review'}
              </p>
            </div>

            {/* Client Action */}
            <div 
              className={`bg-blue-50 rounded-lg p-4 border border-blue-200 ${onShowClientActionItems && metrics.waitingForClient > 0 ? 'cursor-pointer hover:bg-blue-100 hover:shadow-md transition-all' : ''}`}
              onClick={() => {
                if (onShowClientActionItems && metrics.waitingForClient > 0) {
                  onShowClientActionItems();
                }
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-blue-900">Client Action</span>
                <Users className="size-4 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-blue-600">{metrics.waitingForClient}</p>
              <p className="text-xs text-blue-700 mt-1">
                {metrics.waitingForClient > 0 && onShowClientActionItems ? 'Click to view' : 'With client'}
              </p>
            </div>
            
            {/* Scope Controls Tile */}
            {scopeMetrics && (scopeMetrics.undecided > 0 || scopeMetrics.changeOrder > 0) && (
                <div 
                  className={`bg-purple-50 rounded-lg p-4 border border-purple-200 cursor-pointer hover:bg-purple-100 hover:shadow-md transition-all`}
                  onClick={() => onShowScopeControls && onShowScopeControls(scopeMetrics.undecided > 0 ? 'undecided' : 'prepare_change_order')}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-purple-900">Scope Controls</span>
                    <AlertTriangle className="size-4 text-purple-600" />
                  </div>
                  <p className="text-2xl font-bold text-purple-600">{scopeMetrics.undecided}</p>
                  <p className="text-xs text-purple-700 mt-1">
                    {scopeMetrics.changeOrder > 0 ? `+${scopeMetrics.changeOrder} for CO` : 'Items to review'}
                  </p>
                </div>
            )}
          </div>

          {/* Blocked Items Warning */}
          {metrics.blockedItems > 0 && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <AlertTriangle className="size-5 text-red-600" />
              <div>
                <p className="font-medium text-red-900">
                  {metrics.blockedItems} {metrics.blockedItems === 1 ? 'item is' : 'items are'} blocked
                </p>
                <p className="text-sm text-red-700">These items require immediate attention to unblock progress.</p>
              </div>
              {onShowBlockedItems && (
                <Button variant="outline" onClick={onShowBlockedItems} className="ml-auto">
                  Show Blocked Items
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}